import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core';
import { StartDateTypeEnum } from '@app/my-zone/enums';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import {
    DateService,
    ErrorInterface,
    InsurancePolicyActionService,
    PurchasedProductInterface,
} from 'outshared-lib';
import { Subscription } from 'rxjs';
import { NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import {
    WucButtonModule,
    WucDisplayFieldModule,
    WucFormFieldLabelModule,
    WucFormFieldModule, WucInputAlertModule,
    WucInputDatePickerModule, WucRackModule
} from '@inshared/website-ui-components';
import { InsuranceNotificationModule } from '../../insurance-notification';

@Component({
    standalone: true,
    selector: 'ins-insurance-start-date-form',
    templateUrl: './insurance-start-date-form.component.html',
    imports: [
        InsuranceNotificationModule,
        NgIf,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        ReactiveFormsModule,
        WucButtonModule,
        WucDisplayFieldModule,
        WucFormFieldLabelModule,
        WucFormFieldModule,
        WucInputAlertModule,
        WucInputDatePickerModule,
        WucRackModule,
    ]
})
export class InsuranceStartDateFormComponent implements OnDestroy {
    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public startDateType = StartDateTypeEnum.Set;

    public form: UntypedFormGroup;
    public startDateTypeEnum = StartDateTypeEnum;
    public errorNotifications: ErrorInterface[] = [];
    public isLoading: boolean = false;
    private subscriptions: Subscription = new Subscription();

    constructor(
        private readonly dateService: DateService,
        private readonly insurancePolicyActionService: InsurancePolicyActionService,
        private readonly router: Router,
        private readonly changeDetector: ChangeDetectorRef,
    ) {
        this.createForm();
    }

    get startDate(): AbstractControl {
        return this.form.get('startDate');
    }

    private closePanel(): void {
        const queryParams: InsurancesQueryParamsInterface = {
            panel: SelectedPanelEnum.Close,
            productId: this.purchasedProduct.productId,
            productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            scroll: true,
        };

        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge',
        });
    }

    private onStartDateSuccess(): void {
        this.errorNotifications = [];
        this.isLoading = false;

        this.closePanel();
    }

    private onStartDateError(error: ErrorInterface[]): void {
        this.errorNotifications.push(error[0]);
        this.isLoading = false;

        this.changeDetector.detectChanges();
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        this.errorNotifications = [];

        const startDateProduct$ = this.insurancePolicyActionService
            .startDateProduct$({
                productId: this.purchasedProduct.productId,
                productSequenceNumber: this.purchasedProduct.productSequenceNumber,
                startDate: this.startDate.value,
            })
            .subscribe({
                next: () => this.onStartDateSuccess(),
                error: (error: ErrorInterface[]) => this.onStartDateError(error),
            });
        this.subscriptions.add(startDateProduct$);
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            startDate: new FormControl('', [Validators.required]),
        });
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
